<template>
  <v-container fluid class="pa-0">
    <!-- Hero Section -->
    <v-parallax :src="heroImage" height="400" class="hero-section">
      <v-overlay absolute :value="true" opacity="0.6" color="#000"></v-overlay>
      <v-row align="center" justify="center" class="white--text">
        <v-col cols="12" md="8" class="text-center">
          <v-avatar size="120" class="mb-6 elevation-12">
            <v-img contain src="@/assets/logo.png" />
          </v-avatar>
          <h1 class="display-2 font-weight-bold mb-4">NGM - New Global Market Corp.</h1>
          <p class="headline font-weight-light">
            Innovación en soluciones de software empresarial
          </p>
        </v-col>
      </v-row>
    </v-parallax>

    <!-- Main Content -->
    <v-container class="py-12">
      <v-row justify="center">
        <v-col cols="12" lg="10" xl="8">
          <!-- Company Info Card -->
          <v-card class="company-card elevation-0 mb-12" outlined>
            <v-card-text class="pa-8">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="company-info">
                    <h3 class="text-h5 font-weight-bold mb-4 primary--text">
                      Información de Contacto
                    </h3>
                    <div class="info-item mb-3">
                      <v-icon color="primary" class="mr-3">mdi-web</v-icon>
                      <a
                        href="https://newglobalmarket.ca"
                        target="_blank"
                        class="text-decoration-none"
                      >
                        https://newglobalmarket.ca
                      </a>
                    </div>
                    <div class="info-item mb-3">
                      <v-icon color="primary" class="mr-3">mdi-email</v-icon>
                      <a
                        href="mailto:info@newglobalmarket.ca"
                        class="text-decoration-none"
                      >
                        info@newglobal.market
                      </a>
                    </div>
                    <div class="info-item">
                      <v-icon color="primary" class="mr-3">mdi-map-marker</v-icon>
                      <span
                        >2121 Lake Shore Boulevard West<br />Etobicoke, ON, Canada</span
                      >
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="services-info">
                    <h3 class="text-h5 font-weight-bold mb-4 primary--text">
                      Nuestros Servicios
                    </h3>
                    <v-chip-group column>
                      <v-chip
                        v-for="service in services"
                        :key="service.name"
                        label
                        large
                        class="service-chip"
                      >
                        <v-icon left small>{{ service.icon }}</v-icon>
                        {{ service.name }}
                      </v-chip>
                    </v-chip-group>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Terms Sections -->
          <div class="terms-container">
            <!-- Términos y Condiciones -->
            <v-card class="term-card mb-8 elevation-0" outlined>
              <v-card-text class="pa-0">
                <div class="term-header pa-6">
                  <v-icon large color="primary" class="mb-3"
                    >mdi-file-document-outline</v-icon
                  >
                  <h2 class="text-h4 font-weight-bold mb-2">Términos y Condiciones</h2>
                  <p class="text-subtitle-1 grey--text text--darken-1">
                    Última actualización: {{ lastUpdate }}
                  </p>
                </div>

                <v-divider></v-divider>

                <div class="term-content pa-6">
                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">1. Introducción</h3>
                    <p class="text-body-1 text-justify mb-4">
                      Los siguientes Términos y Condiciones de Uso (en adelante
                      <strong>"CONDICIONES"</strong>) regulan el uso de cualquier servicio
                      o producto que provea
                      <strong>NGM - New Global Market Corp.</strong>, así como la relación
                      entre NGM - New Global Market Corp. y sus <strong>USUARIOS</strong>.
                    </p>
                  </div>

                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      2. Clasificación de Servicios
                    </h3>
                    <p class="text-body-1 text-justify mb-3">
                      Los productos y servicios de NGM - New Global Market Corp. se
                      clasifican en:
                    </p>
                    <v-list dense class="mb-4">
                      <v-list-item v-for="item in serviceTypes" :key="item.title">
                        <v-list-item-icon>
                          <v-icon color="primary">{{ item.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            {{ item.title }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ item.description }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </div>

                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      3. Actualizaciones y Modificaciones
                    </h3>
                    <v-alert
                      border="left"
                      colored-border
                      type="info"
                      elevation="0"
                      class="mb-4"
                    >
                      <p class="mb-0">
                        Los productos y servicios de NGM - New Global Market Corp.
                        evolucionarán con el tiempo, por lo que nos reservamos el derecho
                        de modificar estas CONDICIONES, haciéndose efectivas las
                        modificaciones a partir de su publicación en el sitio web oficial.
                      </p>
                    </v-alert>
                  </div>

                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      4. Responsabilidad del Usuario
                    </h3>
                    <v-card flat color="grey lighten-5" class="pa-4 mb-4">
                      <p class="text-body-1 mb-0">
                        <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
                        Es obligación de los USUARIOS asegurarse de haber leído,
                        comprendido y aceptado las CONDICIONES vigentes disponibles en el
                        sitio web oficial de NGM - New Global Market Corp.
                      </p>
                    </v-card>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Políticas -->
            <v-card class="term-card mb-8 elevation-0" outlined>
              <v-card-text class="pa-0">
                <div class="term-header pa-6">
                  <v-icon large color="primary" class="mb-3"
                    >mdi-shield-check-outline</v-icon
                  >
                  <h2 class="text-h4 font-weight-bold mb-2">Políticas de Uso</h2>
                </div>

                <v-divider></v-divider>

                <div class="term-content pa-6">
                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      1. Uso Apropiado de los Servicios
                    </h3>
                    <p class="text-body-1 text-justify mb-4">
                      Debe seguir las políticas disponibles para usted dentro de los
                      Servicios. No utilice nuestros Servicios de forma indebida. Puede
                      utilizar nuestros Servicios solo como se permite por ley, incluidas
                      las leyes y regulaciones correspondientes.
                    </p>

                    <v-alert
                      border="left"
                      colored-border
                      type="warning"
                      elevation="0"
                      class="mb-4"
                    >
                      Podremos suspender o dejar de proveerle nuestros Servicios si usted
                      incumple nuestras condiciones o políticas.
                    </v-alert>
                  </div>

                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      2. Exención de Garantías
                    </h3>
                    <v-card flat color="error lighten-5" class="pa-4 mb-4">
                      <p class="text-body-2 text-uppercase font-weight-bold mb-2">
                        Importante:
                      </p>
                      <p class="text-body-1 mb-0">
                        EXCEPTO POR LO QUE SE ESTABLECE EXPRESAMENTE EN ESTAS CONDICIONES
                        O EN CONDICIONES ADICIONALES, NI NGM CORPORATION NI SUS
                        PROVEEDORES O DISTRIBUIDORES REALIZAN PROMESA ALGUNA ESPECÍFICA
                        SOBRE LOS SERVICIOS.
                      </p>
                    </v-card>

                    <p class="text-body-1 text-justify mb-4">
                      Por ejemplo, no asumimos ningún compromiso respecto al contenido de
                      los servicios, la función específica de los servicios, o su
                      confiabilidad, disponibilidad o capacidad para satisfacer sus
                      necesidades. Proporcionamos los servicios
                      <strong>"TAL COMO ESTÁN"</strong>.
                    </p>
                  </div>

                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      3. Limitación de Responsabilidad
                    </h3>
                    <v-timeline dense align-top>
                      <v-timeline-item color="error" small>
                        <div>
                          <div class="font-weight-medium">No Responsabilidad por Uso</div>
                          <div class="text-body-2">
                            NGM Corporation y sus proveedores no serán responsables por el
                            uso o información ingresada a la plataforma.
                          </div>
                        </div>
                      </v-timeline-item>
                      <v-timeline-item color="error" small>
                        <div>
                          <div class="font-weight-medium">
                            Responsabilidad del Contratante
                          </div>
                          <div class="text-body-2">
                            En todos los casos el contratante asume la totalidad de
                            responsabilidad de la información suministrada por todos los
                            usuarios y empleados.
                          </div>
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Confidencialidad -->
            <v-card class="term-card elevation-0" outlined>
              <v-card-text class="pa-0">
                <div class="term-header pa-6">
                  <v-icon large color="primary" class="mb-3">mdi-lock-outline</v-icon>
                  <h2 class="text-h4 font-weight-bold mb-2">
                    Confidencialidad y Privacidad
                  </h2>
                </div>

                <v-divider></v-divider>

                <div class="term-content pa-6">
                  <div class="term-section">
                    <h3 class="text-h6 font-weight-medium mb-3">
                      Acuerdo de Confidencialidad
                    </h3>
                    <p class="text-body-1 text-justify mb-4">
                      A menos que una de las partes relevantes de este acuerdo cuente con
                      un consentimiento previo escrito de su contraparte:
                    </p>

                    <v-stepper flat class="elevation-0">
                      <v-stepper-items>
                        <v-stepper-content step="1" class="pa-0">
                          <v-card flat>
                            <v-card-text class="pa-4">
                              <div class="d-flex mb-4">
                                <div class="clause-number mr-4">
                                  <v-avatar color="primary" size="40">
                                    <span class="white--text">1.1</span>
                                  </v-avatar>
                                </div>
                                <div class="flex-grow-1">
                                  <p class="text-body-1 mb-0">
                                    Cada parte preservará la confidencialidad de toda la
                                    Información Confidencial de la otra, obtenida en
                                    relación con estas CONDICIONES. Ninguna de las partes
                                    podrá, sin consentimiento escrito previo de la otra,
                                    liberar o mostrar Información Confidencial, o usar la
                                    misma para su propio beneficio o de terceros más allá
                                    de los establecidos en estas CONDICIONES.
                                  </p>
                                </div>
                              </div>

                              <div class="d-flex">
                                <div class="clause-number mr-4">
                                  <v-avatar color="primary" size="40">
                                    <span class="white--text">1.2</span>
                                  </v-avatar>
                                </div>
                                <div class="flex-grow-1">
                                  <p class="text-body-1 mb-0">
                                    Las obligaciones de ambas partes bajo esta cláusula
                                    sobrevivirán a la finalización de estas CONDICIONES.
                                  </p>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-stepper-content>
                      </v-stepper-items>
                    </v-stepper>

                    <v-alert
                      border="left"
                      colored-border
                      color="primary"
                      elevation="0"
                      class="mt-4"
                    >
                      <v-row align="center">
                        <v-col cols="auto">
                          <v-icon large>mdi-information</v-icon>
                        </v-col>
                        <v-col>
                          <p class="mb-0">
                            Este acuerdo de confidencialidad es fundamental para proteger
                            los intereses de ambas partes y garantizar la seguridad de la
                            información compartida.
                          </p>
                        </v-col>
                      </v-row>
                    </v-alert>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Footer Actions -->
          <v-row class="mt-12">
            <v-col cols="12" class="text-center">
              <v-btn large color="primary" class="mr-4" @click="acceptTerms">
                <v-icon left>mdi-check</v-icon>
                Acepto los Términos
              </v-btn>
              <v-btn large outlined color="primary" @click="downloadPDF">
                <v-icon left>mdi-download</v-icon>
                Descargar PDF
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: "About",
  data() {
    return {
      heroImage:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80",
      lastUpdate: new Date().toLocaleDateString("es", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      services: [
        { name: "SaaS", icon: "mdi-cloud" },
        { name: "Licencias de Uso", icon: "mdi-license" },
        { name: "Soporte Técnico", icon: "mdi-tools" },
        { name: "Capacitaciones", icon: "mdi-school" },
        { name: "Certificaciones", icon: "mdi-certificate" },
      ],
      serviceTypes: [
        {
          title: "SaaS (Software as a Service)",
          description: "Soluciones en la nube accesibles desde cualquier lugar",
          icon: "mdi-cloud-outline",
        },
        {
          title: "Licencias de Uso",
          description: "Software con licencia para instalación local",
          icon: "mdi-key-variant",
        },
        {
          title: "Otros Servicios",
          description: "Soporte técnico, capacitaciones y certificaciones",
          icon: "mdi-cog-outline",
        },
      ],
    };
  },
  methods: {
    showMessage(text, color = "success") {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout: 3000,
      };
    },
    acceptTerms() {
      this.showMessage("Términos y condiciones aceptados", "success");
      // Aquí puedes agregar lógica adicional para guardar la aceptación
    },
    downloadPDF() {
      this.showMessage("Descargando PDF...", "info");
      // Aquí puedes agregar la lógica para descargar el PDF
    },
  },
};
</script>

<style scoped>
/* Hero Section */
.hero-section {
  position: relative;
}

/* Company Card */
.company-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.company-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
}

.company-info,
.services-info {
  height: 100%;
}

.info-item {
  display: flex;
  align-items: center;
  color: #424242;
  transition: color 0.2s;
}

.info-item:hover {
  color: #1976d2;
}

.info-item a {
  color: inherit;
}

.service-chip {
  background-color: #f5f5f5 !important;
  border: 1px solid #e0e0e0;
  transition: all 0.2s;
}

.service-chip:hover {
  background-color: #e3f2fd !important;
  border-color: #1976d2;
  transform: translateY(-1px);
}

/* Term Cards */
.term-card {
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.term-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
}

.term-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
  text-align: center;
}

.term-content {
  background-color: white;
}

.term-section {
  margin-bottom: 48px;
}

.term-section:last-child {
  margin-bottom: 0;
}

.term-section h3 {
  color: #424242;
  position: relative;
  padding-left: 20px;
}

.term-section h3::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background-color: #1976d2;
  border-radius: 2px;
}

/* Typography */
.text-justify {
  text-align: justify;
  line-height: 1.7;
}

.text-body-1 {
  color: #616161;
}

/* Timeline */
.v-timeline-item {
  padding-bottom: 24px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.term-card {
  animation: fadeIn 0.6s ease-out;
}

/* Responsive */
@media (max-width: 960px) {
  .term-header {
    padding: 24px !important;
  }

  .term-content {
    padding: 24px !important;
  }

  .term-section {
    margin-bottom: 32px;
  }

  .display-2 {
    font-size: 2.5rem !important;
  }
}

@media (max-width: 600px) {
  .hero-section {
    height: 300px !important;
  }

  .display-2 {
    font-size: 2rem !important;
  }

  .headline {
    font-size: 1.125rem !important;
  }

  .company-card .v-card__text {
    padding: 24px !important;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }

  .text-h5 {
    font-size: 1.25rem !important;
  }

  .text-h6 {
    font-size: 1.125rem !important;
  }
}

/* Print Styles */
@media print {
  .hero-section,
  .v-btn,
  .v-snackbar {
    display: none !important;
  }

  .term-card {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 24px !important;
  }
}
</style>
